import {
  asMetadataFillingProposal,
  asMetadataFillingStarted,
  type MetadataFillingProposalPatched,
} from '@iconik-io/iconik-api/src/schema_patched/metadata_filling';
import {
  httpGetJobsByJobId,
  type JobsJobElasticSchemaWithId,
} from '@iconik-io/iconik-api/src/services/jobs';
import {
  httpGetMetadataFillingProposalsAssetsByAssetIdVersionsByVersionId,
  httpGetMetadataFillingProposalsByJobId,
  httpPostMetadataFillingAssetsByAssetIdVersionsByVersionId,
  httpPostMetadataFillingBulk,
  httpPostMetadataFillingProposalsByJobIdDiscard,
  httpPostMetadataFillingProposalsByParentJobIdBulkAccept,
  httpPostMetadataFillingProposalsByParentJobIdBulkDiscard,
  type MlBulkMetadataFillingSchema,
} from '@iconik-io/iconik-api/src/services/ml';
import { httpPostSearch } from '@iconik-io/iconik-api/src/services/search';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import { isConflictError } from '@iconik-io/iconik-api/src/utils/is_conflict_error';
import { isNotFoundError } from '@iconik-io/iconik-api/src/utils/is_not_found_error';
import { isCancellation } from '@iconik-io/shared-lib/src/redux/middlewares/http/actions';
import { selectMetadataFillingJobsSignatureByObjectId } from '@iconik-io/shared-lib/src/redux/selectors/jobs/selectors';

import { MAX_BULK_OBJECT_IDS } from '../../constants/ai_metadata';
import {
  AI_METADATA_BULK_MODAL,
  AI_METADATA_MODAL,
  AI_METADATA_REVIEW_MODAL,
} from '../../constants/modals';
import type { AppDispatch, GetState } from '../../store/types';
import { getSearchDocument } from '../../utils/get_search_document';
import { segmentTrackEvent } from '../analytics_tracking/thunks';
import { errorMessage, successMessage } from '../messages/thunks';
import { getMetadataView } from '../metadata_view/thunks';
import { closeModal, openModal } from '../modals_manager/slice';
import {
  findAiMetadataGenerationByJobId,
  getAiMetadataGeneration,
  getAiMetadataGenerationKey,
} from './selectors';
import {
  aiMetadataGenerationCleared,
  aiMetadataGenerationFailed,
  aiMetadataGenerationJobIdSet,
  aiMetadataGenerationPendingModalOpenSet,
  aiMetadataGenerationStarted,
  aiMetadataGenerationSucceeded,
  bulkReviewRequestFailed,
  bulkReviewRequestStarted,
  bulkReviewRequestSucceeded,
} from './slice';

const storeAiMetadataGeneration = (
  dispatch: AppDispatch,
  params: {
    assetId: string;
    versionId: string;
    viewId: string;
    selectedFields: string[];
    jobId: string;
    response: MetadataFillingProposalPatched;
  },
): void => {
  const { assetId, versionId, viewId, selectedFields, jobId, response } =
    params;
  dispatch(
    aiMetadataGenerationStarted({ assetId, versionId, viewId, selectedFields }),
  );
  dispatch(aiMetadataGenerationJobIdSet({ assetId, versionId, jobId }));
  dispatch(aiMetadataGenerationSucceeded({ assetId, versionId, response }));
};

export const clearAiMetadataGenerationIfErrored =
  (assetId: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const generations = getState().aiMetadata.generations;
    for (const entry of Object.values(generations)) {
      if (entry.assetId === assetId && entry.status === 'error') {
        dispatch(
          aiMetadataGenerationCleared({
            assetId: entry.assetId,
            versionId: entry.versionId,
          }),
        );
      }
    }
  };

export const reportAiMetadataGenerationError =
  (assetId: string, versionId: string, message: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    const selectModalState = getState().modalsManager[AI_METADATA_MODAL];
    const isSelectModalOpenForThisAsset =
      (selectModalState?.isOpen ?? false) &&
      selectModalState?.contextItems?.[0]?.id === assetId;
    if (isSelectModalOpenForThisAsset) {
      dispatch(
        aiMetadataGenerationFailed({
          assetId,
          versionId,
          errorMessage: message,
        }),
      );
    } else {
      dispatch(errorMessage({ text: message }));
      dispatch(aiMetadataGenerationCleared({ assetId, versionId }));
    }
  };

const storeProposalGeneration = (
  dispatch: AppDispatch,
  getState: GetState,
  assetId: string,
  versionId: string,
  proposal: MetadataFillingProposalPatched,
): string | undefined => {
  const jobId = proposal.job_id;
  const viewId = proposal.view_id;
  if (!jobId || !viewId) {
    return undefined;
  }
  const existing = getAiMetadataGeneration(getState(), assetId, versionId);
  if (
    existing?.status === 'success' &&
    existing.response &&
    existing.jobId === jobId
  ) {
    return jobId;
  }
  storeAiMetadataGeneration(dispatch, {
    assetId,
    versionId,
    viewId,
    selectedFields: proposal.field_names ?? [],
    jobId,
    response: proposal,
  });
  return jobId;
};

export const checkForExistingAiMetadataProposal =
  (assetId: string, versionId: string) =>
  async (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<string | undefined> => {
    try {
      const response = await dispatch(
        httpGetMetadataFillingProposalsAssetsByAssetIdVersionsByVersionId(
          assetId,
          versionId,
        ),
      );
      return storeProposalGeneration(
        dispatch,
        getState,
        assetId,
        versionId,
        asMetadataFillingProposal(response.body),
      );
    } catch (error) {
      if (isCancellation(error) || isNotFoundError(error)) {
        return undefined;
      }
      throw error;
    }
  };

// Reconcile the local generation for a single asset version against the
// version-scoped proposal endpoint (the authority for "is there a proposal for
// this version"). Driven by realtime job updates so the metadata-panel button
// stays correct across tabs: a proposal that appears elsewhere flips the button
// to "Review", and one that is accepted/discarded elsewhere flips it back to
// "Generate". Every write is idempotent.
const reconcileProposal =
  (assetId: string, versionId: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    let jobId: string | undefined;
    try {
      const response = await dispatch(
        httpGetMetadataFillingProposalsAssetsByAssetIdVersionsByVersionId(
          assetId,
          versionId,
        ),
      );
      jobId = storeProposalGeneration(
        dispatch,
        getState,
        assetId,
        versionId,
        asMetadataFillingProposal(response.body),
      );
    } catch (error) {
      // Fire-and-forget realtime sync: a cancellation or transient (non-404)
      // error is swallowed, not re-thrown — there is no caller to handle it and
      // nothing to correct; the current button state is left as-is and the next
      // job update re-runs the reconcile.
      if (isCancellation(error) || !isNotFoundError(error)) {
        return;
      }
      // No proposal for this version. If we were showing a ready proposal, it
      // was accepted or discarded (possibly in another tab) — clear it so the
      // button reverts to "Generate". A same-tab in-flight (`pending`) entry is
      // left alone so we do not wipe the "Generating…" state.
      const existing = getAiMetadataGeneration(getState(), assetId, versionId);
      if (existing?.status === 'success') {
        dispatch(aiMetadataGenerationCleared({ assetId, versionId }));
      }
      return;
    }

    // A proposal exists — make sure its job is in the store so the asset-progress
    // banner shows the pending-review state after a reload (the websocket only
    // pushes live updates, not a job already sitting in PENDING_USER). Only
    // proposal-backed jobs are hydrated, so orphaned jobs whose proposal was
    // stripped after 30 days never resurface as banners. Guarded on absence so
    // it hydrates once and does not re-loop its own realtime trigger, and
    // isolated so a job-fetch error cannot clear the proposal reconciled above.
    if (jobId && !getState().entities.jobs[jobId]) {
      try {
        await dispatch(httpGetJobsByJobId(jobId));
      } catch {
        // Ignore — the button state is already correct; only the banner is
        // affected.
      }
    }
  };

const reconcilesInFlight = new Map<
  string,
  { jobsSignature: string; promise: Promise<void> }
>();

// Several components reconcile the same version for the same job change (the
// asset page and the metadata-panel button), so they share one request. A
// later job change starts a new one.
export const reconcileAiMetadataProposal =
  (assetId: string, versionId: string) =>
  (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const key = getAiMetadataGenerationKey(assetId, versionId);
    const jobsSignature = selectMetadataFillingJobsSignatureByObjectId(
      getState(),
      assetId,
    );
    const inFlight = reconcilesInFlight.get(key);
    if (inFlight?.jobsSignature === jobsSignature) {
      return inFlight.promise;
    }
    const promise = dispatch(reconcileProposal(assetId, versionId)).finally(
      () => {
        if (reconcilesInFlight.get(key)?.promise === promise) {
          reconcilesInFlight.delete(key);
        }
      },
    );
    reconcilesInFlight.set(key, { jobsSignature, promise });
    return promise;
  };

const settle = <T>(promise: Promise<T>): Promise<PromiseSettledResult<T>> =>
  promise.then(
    (value): PromiseSettledResult<T> => ({ status: 'fulfilled', value }),
    (reason): PromiseSettledResult<T> => ({ status: 'rejected', reason }),
  );

export const startAiMetadataGeneration =
  (
    assetId: string,
    versionId: string,
    viewId: string,
    selectedFields: string[],
    autoAccept: boolean,
  ) =>
  async (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<string | undefined> => {
    dispatch(
      aiMetadataGenerationStarted({
        assetId,
        versionId,
        viewId,
        selectedFields,
        autoAccept,
      }),
    );

    const postMetadataFilling = async (): Promise<string> => {
      const startResponse = await dispatch(
        httpPostMetadataFillingAssetsByAssetIdVersionsByVersionId(
          assetId,
          versionId,
          {
            field_names: selectedFields,
            view_id: viewId,
            auto_accept: autoAccept,
          },
        ),
      );
      const startBody = asMetadataFillingStarted(startResponse.body);
      const jobId = startBody.job_id ?? startBody.id;
      if (!jobId) {
        throw new Error('Missing job id in response');
      }
      dispatch(aiMetadataGenerationJobIdSet({ assetId, versionId, jobId }));
      dispatch(
        successMessage({
          text: autoAccept
            ? 'AI metadata generation started. Values are saved to the fields when ready.'
            : 'AI metadata generation started',
        }),
      );
      dispatch(closeModal({ id: AI_METADATA_MODAL }));
      return jobId;
    };

    const openReviewForExistingProposal = (proposalJobId: string): string => {
      const state = getState();
      const asset =
        state.entities.assets[assetId] ?? state.entities.search_items[assetId];
      if (asset) {
        dispatch(closeModal({ id: AI_METADATA_MODAL }));
        dispatch(
          openModal({
            id: AI_METADATA_REVIEW_MODAL,
            contextItems: [asset],
            options: { versionId },
          }),
        );
      }
      return proposalJobId;
    };

    const reportFailure = (): undefined => {
      dispatch(
        reportAiMetadataGenerationError(
          assetId,
          versionId,
          'Failed to generate AI metadata. Please try again.',
        ),
      );
      return undefined;
    };

    const attempt = await settle(postMetadataFilling());
    if (attempt.status === 'fulfilled') {
      return attempt.value;
    }
    if (isCancellation(attempt.reason)) {
      return undefined;
    }

    // A 409 means a proposal already exists for this asset version — open it
    // for review instead of surfacing an error. The backend can also return a
    // 409 spuriously against stale state (no proposal, nothing running); that
    // falls through to the generic message below. Removing that spurious
    // conflict is a backend follow-up (see FE-7108).
    if (isConflictError(attempt.reason)) {
      const proposal = await settle(
        dispatch(checkForExistingAiMetadataProposal(assetId, versionId)),
      );
      if (proposal.status === 'fulfilled' && proposal.value) {
        return openReviewForExistingProposal(proposal.value);
      }
    }

    return reportFailure();
  };

export const startBulkAiMetadataGeneration =
  (
    objectType: MlBulkMetadataFillingSchema['object_type'],
    objectIds: string[],
    viewId: string,
    fieldNames: string[],
    autoAccept: boolean,
  ) =>
  async (dispatch: AppDispatch): Promise<void> => {
    if (objectIds.length === 0 || fieldNames.length === 0) {
      return;
    }

    try {
      await dispatch(
        httpPostMetadataFillingBulk({
          field_names: fieldNames,
          object_ids: objectIds.slice(0, MAX_BULK_OBJECT_IDS),
          object_type: objectType,
          view_id: viewId,
          auto_accept: autoAccept,
        }),
      );
      dispatch(closeModal({ id: AI_METADATA_BULK_MODAL }));
      dispatch(
        successMessage({
          text: autoAccept
            ? 'AI metadata generation started. Values are saved to the fields when ready.'
            : 'AI metadata generation started. This operation can take some time.',
        }),
      );
    } catch (error) {
      if (isCancellation(error)) {
        return;
      }
      dispatch(
        errorMessage({
          text: 'Failed to start AI metadata generation. Please try again.',
        }),
      );
      throw error;
    }
  };

export const openAiMetadataReviewFromJobPage =
  (assetId: string, jobId: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const existingBeforeFetch = findAiMetadataGenerationByJobId(
      getState(),
      jobId,
    );
    if (
      existingBeforeFetch &&
      existingBeforeFetch.assetId === assetId &&
      existingBeforeFetch.status === 'success'
    ) {
      dispatch(
        segmentTrackEvent({
          eventName: 'Opened Review AI-Generated Metadata Modal',
          properties: {
            Source: 'job page',
          },
        }),
      );
      dispatch(
        aiMetadataGenerationPendingModalOpenSet({
          assetId: existingBeforeFetch.assetId,
          versionId: existingBeforeFetch.versionId,
        }),
      );
      return;
    }

    try {
      const proposalResponse = await dispatch(
        httpGetMetadataFillingProposalsByJobId(jobId),
      );
      const proposal = asMetadataFillingProposal(proposalResponse.body);

      const viewId = proposal.view_id;
      const versionId = proposal.version_id;
      if (!viewId || !versionId) {
        dispatch(
          errorMessage({
            text: 'AI metadata proposal is missing view or version information.',
          }),
        );
        return;
      }

      if (proposal.asset_id && proposal.asset_id !== assetId) {
        dispatch(
          errorMessage({
            text: 'AI metadata proposal does not match the current asset.',
          }),
        );
        return;
      }

      const selectedFields = proposal.field_names ?? [];

      const existingAfterFetch = getAiMetadataGeneration(
        getState(),
        assetId,
        versionId,
      );
      const isAlreadyHydrated =
        existingAfterFetch?.status === 'success' &&
        !!existingAfterFetch.response;
      if (!isAlreadyHydrated) {
        storeAiMetadataGeneration(dispatch, {
          assetId,
          versionId,
          viewId,
          selectedFields,
          jobId,
          response: proposal,
        });
      }
      dispatch(
        segmentTrackEvent({
          eventName: 'Opened Review AI-Generated Metadata Modal',
          properties: {
            Source: 'job page',
          },
        }),
      );
      dispatch(aiMetadataGenerationPendingModalOpenSet({ assetId, versionId }));
    } catch (error) {
      if (isCancellation(error)) {
        return;
      }
      dispatch(
        errorMessage({
          text: 'Failed to load AI metadata proposal.',
        }),
      );
    }
  };

// Loads a job's proposal, with its view and asset, for the AI metadata
// enrichment page.
export type AiMetadataProposalLoadResult =
  | 'loaded'
  | 'accepted'
  | 'rejected'
  | 'cancelled';

export const loadAiMetadataProposalForJob =
  (jobId: string) =>
  async (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<AiMetadataProposalLoadResult> => {
    // Already loaded, e.g. on the asset page; storing it again would reset the
    // user's choices.
    const alreadyLoaded = findAiMetadataGenerationByJobId(getState(), jobId);
    if (alreadyLoaded?.status === 'success' && alreadyLoaded.response) {
      return 'loaded';
    }

    let proposal: MetadataFillingProposalPatched;
    try {
      const proposalResponse = await dispatch(
        httpGetMetadataFillingProposalsByJobId(jobId),
      );
      proposal = asMetadataFillingProposal(proposalResponse.body);
    } catch (error) {
      if (isCancellation(error)) {
        return 'cancelled';
      }
      throw error;
    }

    // Already accepted or discarded, though the job shown may not say so yet.
    if (proposal.review_status === 'ACCEPTED') {
      return 'accepted';
    }
    if (proposal.review_status === 'REJECTED') {
      return 'rejected';
    }

    const assetId = proposal.asset_id ?? undefined;
    const versionId = proposal.version_id ?? undefined;
    const viewId = proposal.view_id ?? undefined;
    if (!assetId || !versionId || !viewId) {
      throw new Error(
        'This AI metadata proposal is missing view, version, or asset information.',
      );
    }

    // The review UI reads the view's field definitions from
    // `entities.metadata_views`, which the admin page doesn't preload.
    if (!getState().entities.metadata_views[viewId]) {
      dispatch(getMetadataView(viewId));
    }

    // Kept when it's this job's proposal, so the user's choices survive. One
    // from another job on the same asset version is replaced.
    const existing = getAiMetadataGeneration(getState(), assetId, versionId);
    const isLoadedForJob =
      existing?.status === 'success' &&
      Boolean(existing.response) &&
      existing.jobId === jobId;
    if (!isLoadedForJob) {
      storeAiMetadataGeneration(dispatch, {
        assetId,
        versionId,
        viewId,
        selectedFields: proposal.field_names ?? [],
        jobId,
        response: proposal,
      });
    }

    // Saving reads the asset's versions, so make sure the asset is loaded.
    if (!getState().entities.search_items[assetId]) {
      dispatch(
        httpPostSearch(getSearchDocument([assetId], [OBJECT_TYPE_ASSETS]), {
          save_search_history: false,
          per_page: 1,
        }),
      );
    }

    return 'loaded';
  };

// The generic http error toast is disabled for these endpoints, so the failure
// message is shown here.
const requestBulkProposalReview =
  (parentJobId: string, status: 'updated' | 'discarded') =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    try {
      await dispatch(
        status === 'updated'
          ? httpPostMetadataFillingProposalsByParentJobIdBulkAccept(parentJobId)
          : httpPostMetadataFillingProposalsByParentJobIdBulkDiscard(
              parentJobId,
            ),
      );
      return true;
    } catch (error) {
      if (!isCancellation(error)) {
        dispatch(
          errorMessage({
            text:
              status === 'updated'
                ? 'Failed to accept the AI-generated metadata.'
                : 'Failed to discard the AI-generated metadata.',
          }),
        );
      }
      return false;
    }
  };

export const reviewAllPendingProposals =
  ({
    job,
    status,
  }: {
    job: JobsJobElasticSchemaWithId;
    status: 'updated' | 'discarded';
  }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(bulkReviewRequestStarted({ parentJobId: job.id, status }));
    const isRequested = await dispatch(
      requestBulkProposalReview(job.id, status),
    );
    if (!isRequested) {
      // Let the user try again.
      dispatch(bulkReviewRequestFailed(job.id));
      return;
    }
    dispatch(bulkReviewRequestSucceeded(job.id));
    dispatch(
      successMessage({
        text:
          status === 'updated'
            ? 'Accepting the AI-generated metadata for all pending assets in the background.'
            : 'Discarding the AI-generated metadata for all pending assets in the background.',
      }),
    );
    dispatch(
      segmentTrackEvent({
        eventName: 'Applied AI Metadata Review To All Pending Assets',
        properties: {
          Action: status === 'updated' ? 'update' : 'discard',
          Source: 'ai metadata enrichment admin page',
        },
      }),
    );
  };

// Discards a job's proposal from the job menu, without opening the review.
export const discardAiMetadataProposalForJob =
  (job: JobsJobElasticSchemaWithId) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    try {
      await dispatch(httpPostMetadataFillingProposalsByJobIdDiscard(job.id));
    } catch {
      dispatch(
        errorMessage({
          text: 'Failed to discard metadata proposal. Please try again.',
        }),
      );
      return false;
    }
    // Pick up the new status even if the websocket update is slow.
    dispatch(httpGetJobsByJobId(job.id));
    dispatch(successMessage({ text: 'AI-generated metadata discarded.' }));
    dispatch(
      segmentTrackEvent({
        eventName: 'Metadata Discarded',
        properties: {
          Entity: 'assets',
          Bulk: false,
          Source: 'ai metadata enrichment admin page',
        },
      }),
    );
    return true;
  };
